import { ApiError } from './api-error';
import { NetworkError } from './network-error';

export interface HttpClientOptions {
  /** Prefix of every path, e.g. "/api". */
  readonly baseUrl: string;
  readonly getToken: () => string | null;
}

export interface RequestOptions extends RequestInit {
  readonly timeoutMs?: number;
}

const UNREACHABLE = new Set([502, 503, 504]);

export class HttpClient {
  constructor(private readonly options: HttpClientOptions) {}

  async request(path: string, init: RequestOptions = {}): Promise<Response> {
    const { timeoutMs = 30_000, ...rest } = init;
    const headers = new Headers(rest.headers);
    const token = this.options.getToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    if (typeof rest.body === 'string' && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    let response: Response;
    try {
      response = await fetch(`${this.options.baseUrl}${path}`, {
        ...rest,
        headers,
        signal: rest.signal ?? AbortSignal.timeout(timeoutMs),
        cache: 'no-store',
      });
    } catch (error) {
      throw new NetworkError(error instanceof Error ? error.message : 'offline');
    }
    if (UNREACHABLE.has(response.status)) throw new NetworkError(`HTTP ${response.status}`);
    if (!response.ok) {
      const body: unknown = await response.json().catch(() => null);
      const envelope = (typeof body === 'object' && body !== null ? body : {}) as {
        code?: string;
        message?: string;
        details?: unknown;
      };
      throw new ApiError(
        response.status,
        envelope.code ?? `http.${response.status}`,
        envelope.message ?? response.statusText,
        envelope.details,
      );
    }
    return response;
  }

  async json<T>(path: string, init: RequestOptions = {}): Promise<T> {
    const response = await this.request(path, init);
    if (response.status === 204) return undefined as T;
    // The protocol's response shapes are fixed (docs/modules/sync.md); callers name them.
    return (await response.json()) as T;
  }
}
